(function () {
  // Expose sync pause flag globally for coordination
  window._cartSyncPaused = false;

  // --- Cookie helpers ---
  function setCookieCustomTheme(name, value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 86400000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = `${name}=${value || ""}${expires}; path=/`;
  }

  function getCookieCustomTheme(name) {
    return document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] || null;
  }

  function eraseCookieCustomTheme(name) {
    setCookieCustomTheme(name, '', -1);
  }

  function setBodyLoader(state) {
    document.body.classList.toggle("loading-drawer", state);
  }
  
  function getMySectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // ✅ Helper: Check if variant is a special product (gift/shipping) - DYNAMIC
  function isSpecialProduct(variantId) {
    const shipVariantId = window.Shopify?.shipping_protection_variant;
    const giftVariantId = window.Shopify?.select_gift_card_product;
    const giftVariantId2 = window.Shopify?.select_gift_card_product_2;
    const specialIds = [shipVariantId, giftVariantId, giftVariantId2].filter(Boolean);
    return specialIds.includes(Number(variantId));
  }

  // --- Refresh Cart Drawer (for Dawn 2.0) ---
  async function refreshCartDrawer() {
    const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
    try {
      const response = await fetch('/?sections=' + sectionsToRender.join(','));
      const data = await response.json();
      
      const cartDrawer = document.getElementById('CartDrawer');
      if (cartDrawer) {
        cartDrawer.querySelector('.drawer__inner').innerHTML = getMySectionInnerHTML(
          data['cart-drawer'],
          '.drawer__inner'
        );
      }

      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble) {
        cartBubble.innerHTML = getMySectionInnerHTML(data['cart-icon-bubble']);
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  // --- Add Product to Cart ---
  async function addProductToCart(variantId, options = {}) {
    try {
      setBodyLoader(true);

      const payload = {
        id: variantId,
        quantity: options.quantity || 1,
        properties: {
          '_cartaddon': 'yes'
        }
      };
      

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        await refreshCartDrawer();
      }
    } finally {
      setBodyLoader(false);
    }
  }

  // --- Remove Product from Cart ---
  async function removeProductFromCart(identifier) {
    try {
      setBodyLoader(true);

      // If identifier looks like a line key (string, typically > 20 chars)
      if (typeof identifier === 'string' && identifier.length > 20) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: identifier, quantity: 0 }),
        });
        await refreshCartDrawer();
        return;
      }

      // Otherwise assume variant id — remove by line key
      const cart = await fetch("/cart.js").then((r) => r.json());
      const match = cart.items.find((i) => Number(i.variant_id) === Number(identifier));
      
      if (match) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: match.key, quantity: 0 }),
        });
      } else {
        // Fallback to update (removes all lines with the variant)
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates: { [identifier]: 0 } }),
        });
      }

      await refreshCartDrawer();
    } catch (err) {
      console.error("removeProductFromCart failed:", err);
    } finally {
      setBodyLoader(false);
    }
  }

  // --- Bundle Tracking for Complete Removal ---
  let _previousCartBundles = {};
  
  // ✅ Get all bundle IDs and their items from cart
  function getBundlesFromCart(cart) {
    const bundles = {};
    for (const item of cart.items) {
      // ✅ Must match the property name written by product-form.js (_sh_bundle_id)
      const bundleId = item.properties?._sh_bundle_id;
      if (bundleId) {
        if (!bundles[bundleId]) {
          bundles[bundleId] = [];
        }
        bundles[bundleId].push(item);
      }
    }
    return bundles;
  }
  
  // ✅ Handle bundle removal - if any item from bundle is removed, remove all
  async function handleBundleRemoval(cart) {
    const currentBundles = getBundlesFromCart(cart);
    let bundleWasRemoved = false;
    
    // Check if any bundle is now incomplete (was in previous, items reduced)
    for (const [bundleId, previousItems] of Object.entries(_previousCartBundles)) {
      const currentItems = currentBundles[bundleId] || [];
      
      // If bundle existed before and now has fewer items, remove all remaining
      if (previousItems.length > 0 && currentItems.length > 0 && currentItems.length < previousItems.length) {
        console.log(`Bundle ${bundleId} is incomplete. Removing remaining ${currentItems.length} items.`);
        
        // Remove all remaining items from this bundle
        for (const item of currentItems) {
          try {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ id: item.key, quantity: 0 }),
            });
            bundleWasRemoved = true;
          } catch (err) {
            console.warn('Failed to remove bundle item:', err);
          }
        }
        
        // Remove this bundle from tracking
        delete currentBundles[bundleId];
      }
    }
    
    // Update stored bundles for next comparison
    _previousCartBundles = currentBundles;
    
    return bundleWasRemoved;
  }
  
  // ✅ Initialize bundle tracking on page load
  async function initBundleTracking() {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      _previousCartBundles = getBundlesFromCart(cart);
    } catch (err) {
      console.warn('Failed to initialize bundle tracking:', err);
    }
  }

  // --- Sync Logic ---
  let _syncInProgress = false;
  let _syncPending = false;

  async function syncCartProducts() {
    // If already running, schedule another run and return
    if (_syncInProgress) {
      _syncPending = true;
      return;
    }

    _syncInProgress = true;
    window._cartSyncPaused = true;

    try {
      setBodyLoader(true);
      let response = await fetch("/cart.js");
      let cart = await response.json();
      
      // ✅ BUNDLE REMOVAL LOGIC: Check for incomplete bundles
      const bundleWasRemoved = await handleBundleRemoval(cart);
      
      // If bundles were removed, re-fetch the cart
      if (bundleWasRemoved) {
        response = await fetch("/cart.js");
        cart = await response.json();
      }

      // ✅ DYNAMIC: Get configured products (may be undefined if not configured)
      const shipVariantId = window.Shopify?.shipping_protection_variant;
      const giftVariantId = window.Shopify?.select_gift_card_product;
      const giftVariantId2 = window.Shopify?.select_gift_card_product_2;

      const giftThreshold = Number(window.Shopify?.select_gift_threshold) || 0;
      const giftThreshold2 = Number(window.Shopify?.select_gift_threshold_2) || 0;
      const freeShippingThreshold = Number(window.Shopify?.free_shipping_threshold) || 0;

      const giftProductHandle = window.Shopify?.select_gift_card_product_handle;
      const giftProductHandle2 = window.Shopify?.select_gift_card_product_2_handle;
      const shipProductHandle = window.Shopify?.shipping_protection_handle;

      // ✅ DYNAMIC: Build list of special product handles (only configured ones)
      const specialHandles = [shipProductHandle, giftProductHandle, giftProductHandle2].filter(Boolean);

      // ✅ DYNAMIC: Filter cart items based on configured products
      const giftItems = giftProductHandle ? cart.items.filter((i) => i.handle === giftProductHandle) : [];
      const giftItems2 = giftProductHandle2 ? cart.items.filter((i) => i.handle === giftProductHandle2) : [];
      const shipItems = shipProductHandle ? cart.items.filter((i) => i.handle === shipProductHandle) : [];

      const userRemovedProtection = getCookieCustomTheme("shipProtectCookie") === "removed";

      // ✅ DYNAMIC: Check for normal items (excluding only configured special products)
      const hasNormalItems = cart.items.some(
        (i) => !specialHandles.includes(i.handle)
      );

      // ✅ DYNAMIC: Calculate subtotal (excluding only configured special products)
      const subtotal = cart.items
        .filter((i) => !specialHandles.includes(i.handle))
        .reduce((sum, i) => sum + i.final_line_price / 100, 0);

      // ✅ DYNAMIC: Auto add/remove shipping protection (only if configured)
      if (shipVariantId && shipProductHandle) {
        if (hasNormalItems && !shipItems.length && !userRemovedProtection) {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ 
              id: shipVariantId, 
              quantity: 1, 
              properties: { '_cartaddon': 'yes' } 
            }),
          });
        } else if (!hasNormalItems && shipItems.length) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: shipItems[0].key, quantity: 0 }),
          });
        }
      }

      // ✅ DYNAMIC: Gift 1 logic (only if configured)
      if (giftVariantId && giftProductHandle && giftThreshold > 0) {
        // Determine upper bound: if Gift 2 is configured, use its threshold; otherwise no upper bound
        const hasUpperBound = giftVariantId2 && giftThreshold2 > 0;
        const shouldAddGift1 = subtotal >= giftThreshold && (!hasUpperBound || subtotal < giftThreshold2);
        const shouldRemoveGift1 = subtotal < giftThreshold || (hasUpperBound && subtotal >= giftThreshold2);

        if (!giftItems.length && shouldAddGift1) {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ 
              id: giftVariantId, 
              quantity: 1, 
              properties: { '_cartaddon': 'yes' } 
            }),
          });
        } else if (giftItems.length && shouldRemoveGift1) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: giftItems[0].key, quantity: 0 }),
          });
        }
      }

      // ✅ DYNAMIC: Gift 2 logic (only if configured)
      if (giftVariantId2 && giftProductHandle2 && giftThreshold2 > 0) {
        if (subtotal >= giftThreshold2 && !giftItems2.length) {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ 
              id: giftVariantId2, 
              quantity: 1, 
              properties: { '_cartaddon': 'yes' } 
            }),
          });
        } else if (subtotal < giftThreshold2 && giftItems2.length) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: giftItems2[0].key, quantity: 0 }),
          });
        }
      }

      // ✅ DYNAMIC: Clean properties + fix quantity for all special products
      const allSpecialItems = [...giftItems, ...giftItems2, ...shipItems];
      for (const item of allSpecialItems) {
        const hasProperties = item.properties && Object.keys(item.properties).length > 0;
        const wrongQuantity = item.quantity !== 1;
        
        if (hasProperties || wrongQuantity) {
          try {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                id: item.key, 
                quantity: 1,
                properties: { '_cartaddon': 'yes' }
              }),
            });
          } catch (err) {
            console.warn('Failed to fix special item:', err);
          }
        }
      }

      // ✅ DYNAMIC: Remove duplicates for each configured product
      if (giftItems.length > 1) {
        for (let i = 1; i < giftItems.length; i++) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: giftItems[i].key, quantity: 0 }),
          });
        }
      }

      if (giftItems2.length > 1) {
        for (let i = 1; i < giftItems2.length; i++) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: giftItems2[i].key, quantity: 0 }),
          });
        }
      }

      if (shipItems.length > 1) {
        for (let i = 1; i < shipItems.length; i++) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: shipItems[i].key, quantity: 0 }),
          });
        }
      }

      // ✅ Remove orphaned FREE bundle addons
      // A free PDP addon is only ever added alongside its parent product (same _sh_bundle_id).
      // If its bundle has no other line left, the parent was removed and the addon is a leftover.
      // Guarded: only $0.00 lines, only bundles of size 1, and only when real items remain in cart.
      try {
        const bundleCart = await fetch("/cart.js").then((r) => r.json());

        const bundleCounts = {};
        for (const item of bundleCart.items) {
          const bId = item.properties?._sh_bundle_id;
          if (bId) bundleCounts[bId] = (bundleCounts[bId] || 0) + 1;
        }

        const orphanFreeAddons = bundleCart.items.filter((item) => {
          const bId = item.properties?._sh_bundle_id;
          return bId && bundleCounts[bId] === 1 && item.final_line_price === 0;
        });

        const hasRealItems = bundleCart.items.some(
          (item) =>
            item.final_line_price > 0 &&
            item.properties?._cartaddon !== "yes"
        );

        if (orphanFreeAddons.length && hasRealItems) {
          for (const item of orphanFreeAddons) {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ id: item.key, quantity: 0 }),
            });
          }

          // Re-sync bundle tracking so the next run doesn't compare against a stale snapshot
          const afterCart = await fetch("/cart.js").then((r) => r.json());
          _previousCartBundles = getBundlesFromCart(afterCart);
          cart = afterCart;
        }
      } catch (err) {
        console.warn("Orphan addon cleanup failed:", err);
      }

      // ✅ If only addon products remain → empty cart
      const onlyAddonItems =
        cart.items.length > 0 &&
        cart.items.every(
          (i) =>
            i.properties?._cartaddon === "yes"
        );

      if (onlyAddonItems) {
        for (const i of cart.items) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: i.key, quantity: 0 }),
          });
        }
        document.querySelector("cart-drawer")?.classList.add("is-empty");
      }

      // ✅ Single refresh at the end only
      await refreshCartDrawer();
      
    } catch (err) {
      console.error("Error syncing cart:", err);
    } finally {
      setBodyLoader(false);
      _syncInProgress = false;
      window._cartSyncPaused = false;
      
      // If another sync was requested during execution, run it
      if (_syncPending) {
        _syncPending = false;
        setTimeout(syncCartProducts, 250);
      }
    }
  }

  // ✅ Initialize sync on page load and cart:refresh events
  document.addEventListener("DOMContentLoaded", async () => {
    // Initialize bundle tracking first
    await initBundleTracking();
    // Then run sync
    syncCartProducts();
  });
  document.addEventListener("cart:refresh", syncCartProducts);

  // --- Manual Shipping Protection Checkbox ---
  document.addEventListener("change", (e) => {
    const toggler = e.target.closest("#cartShipProtectionToggler");
    if (!toggler) return;

    const variantId = toggler.getAttribute("data-value");

    if (toggler.checked) {
      eraseCookieCustomTheme("shipProtectCookie");
      // ✅ Add with empty properties
      addProductToCart(variantId);
    } else {
      removeProductFromCart(variantId);
      setCookieCustomTheme("shipProtectCookie", "removed", 30);
    }
  });

  // --- Upsell Products on Cart Drawer ---
  document.addEventListener('click', async function(event) {
    if (!event.target.classList.contains('add-upsell-to-cart')) return;

    const productId = event.target.getAttribute('data-product-id');
    // ✅ Upsell products can have properties (they're not special)
    await addProductToCart(productId);
  });

  // --- Expand hidden text sections ---
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('.more_trigger');
    if (!trigger) return;

    const parent = trigger.parentElement;
    if (!parent) return;

    parent.classList.add('active');
  });

})();


let items = document.querySelectorAll(".mega-menu");
let header = document.querySelector("header");
let currentItemIndex;

items.forEach((item, itemIndex) => {
  const content = item.querySelector(".mega-menu__content");

  item.addEventListener("mouseover", () => {
    if (
      (currentItemIndex === 0 && currentItemIndex !== itemIndex) ||
      (currentItemIndex && currentItemIndex !== itemIndex)
    ) {
      items[currentItemIndex].removeAttribute("open");
    }

    currentItemIndex = itemIndex;
    item.setAttribute("open", true);
  });

  item.addEventListener("mouseleave", (event) => {
    let close = true;

    const closeTimeout = setTimeout(() => {
      if (close) item.removeAttribute("open");
    }, 500);

    content.addEventListener("mouseover", () => {
      close = false;
    });

    item.addEventListener("mouseover", () => {
      clearTimeout(closeTimeout);
    });
  });

  content.addEventListener("mouseleave", () => {
    item.removeAttribute("open");
  });
});

document.addEventListener("DOMContentLoaded", function () {
  const btn = document.querySelector(".top-close-button");
  if (btn) {
    btn.addEventListener("click", function () {
      document.body.classList.remove("overflow-hidden-tablet");
    });
  }
});